import { useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three/webgpu';
import { uv, vec3, vec4, float, uniform, smoothstep } from 'three/tsl';

// A small pool of meteor streaks that periodically dart across the sky dome.
// Sits just inside the Stars shell (radius 190 * rim 0.95) and follows the
// camera the same way Stars does, so streaks always read as sky.
const POOL_SIZE = 3;
const RADIUS = 165;
const MIN_DELAY = 3;
const MAX_DELAY = 10;
const STREAK_COLOR = new THREE.Color('#cfe4ff');

interface Streak {
  envelope: ReturnType<typeof uniform>;
  material: THREE.MeshBasicNodeMaterial;
  active: boolean;
  wait: number;
  t: number;
  duration: number;
  from: THREE.Vector3;
  to: THREE.Vector3;
}

const randomDelay = () => MIN_DELAY + Math.random() * (MAX_DELAY - MIN_DELAY);

function randomUpperDirection(minY: number): THREE.Vector3 {
  const v = new THREE.Vector3();
  do {
    v.set(Math.random() * 2 - 1, Math.random(), Math.random() * 2 - 1);
  } while (v.lengthSq() > 1 || v.lengthSq() === 0 || v.clone().normalize().y < minY);
  return v.normalize();
}

function makeStreak(): Streak {
  const envelope = uniform(0);

  const material = new THREE.MeshBasicNodeMaterial();
  material.transparent = true;
  material.depthWrite = false;
  material.blending = THREE.AdditiveBlending;
  material.side = THREE.DoubleSide;

  // uv().x runs tail (0) to head (1); soften edges across the thickness
  const tail = smoothstep(float(0.0), float(0.85), uv().x);
  const across = smoothstep(float(0.5), float(0.1), uv().y.sub(0.5).abs());
  const alpha = tail.mul(across).mul(envelope);
  material.colorNode = vec4(
    vec3(STREAK_COLOR.r, STREAK_COLOR.g, STREAK_COLOR.b).mul(2.5),
    alpha
  );

  return {
    envelope,
    material,
    active: false,
    wait: randomDelay(),
    t: 0,
    duration: 1,
    from: new THREE.Vector3(),
    to: new THREE.Vector3(),
  };
}

export function ShootingStars() {
  const { camera } = useThree();
  const groupRef = useRef<THREE.Group>(null);
  const meshRefs = useRef<(THREE.Mesh | null)[]>([]);

  const streaks = useMemo(() => Array.from({ length: POOL_SIZE }, makeStreak), []);

  // Scratch objects reused every frame to avoid GC pressure
  const scratch = useMemo(
    () => ({
      pos: new THREE.Vector3(),
      zAxis: new THREE.Vector3(),
      xAxis: new THREE.Vector3(),
      yAxis: new THREE.Vector3(),
      travel: new THREE.Vector3(),
      basis: new THREE.Matrix4(),
    }),
    []
  );

  useFrame((_, rawDelta) => {
    const delta = Math.min(rawDelta, 0.1);
    const group = groupRef.current;
    if (!group) return;
    group.position.copy(camera.position);

    streaks.forEach((s, i) => {
      const mesh = meshRefs.current[i];
      if (!mesh) return;

      if (!s.active) {
        s.wait -= delta;
        mesh.visible = false;
        if (s.wait <= 0) {
          // Launch: pick a start point high in the sky and a nearby end point
          const start = randomUpperDirection(0.25);
          const tangent = new THREE.Vector3(
            Math.random() * 2 - 1,
            Math.random() * 0.4 - 0.35,
            Math.random() * 2 - 1
          )
            .normalize()
            .multiplyScalar(30 + Math.random() * 40);
          s.from.copy(start).multiplyScalar(RADIUS);
          s.to.copy(s.from).add(tangent).normalize().multiplyScalar(RADIUS);
          s.duration = 0.6 + Math.random() * 0.7;
          s.t = 0;
          s.active = true;
        }
        return;
      }

      s.t += delta;
      const k = s.t / s.duration;
      if (k >= 1) {
        s.active = false;
        s.wait = randomDelay();
        s.envelope.value = 0;
        mesh.visible = false;
        return;
      }

      mesh.visible = true;
      // Fade in fast, fade out slow
      s.envelope.value = Math.sin(Math.min(k * 1.5, 1) * Math.PI) * 0.9;

      const { pos, zAxis, xAxis, yAxis, travel, basis } = scratch;
      pos.lerpVectors(s.from, s.to, k).normalize().multiplyScalar(RADIUS);
      mesh.position.copy(pos);

      // Billboard the streak: face the camera, x axis along travel direction
      travel.subVectors(s.to, s.from).normalize();
      zAxis.copy(pos).negate().normalize();
      xAxis.copy(travel).addScaledVector(zAxis, -travel.dot(zAxis)).normalize();
      yAxis.crossVectors(zAxis, xAxis);
      basis.makeBasis(xAxis, yAxis, zAxis);
      mesh.quaternion.setFromRotationMatrix(basis);
    });
  });

  return (
    <group ref={groupRef}>
      {streaks.map((s, i) => (
        <mesh
          key={i}
          ref={(m) => (meshRefs.current[i] = m)}
          material={s.material}
          visible={false}
          frustumCulled={false}
          scale={[14, 0.35, 1]}
        >
          <planeGeometry args={[1, 1]} />
        </mesh>
      ))}
    </group>
  );
}
